/**
 * DropZone Component
 *
 * Drag-and-drop file upload zone for PDF medical history documents.
 * Supports single and multiple file selection.
 */

import { useCallback, useState, useRef } from 'react';
import { Upload, FileText } from 'lucide-react';

interface DropZoneProps {
  onFilesSelected: (files: File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
}

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['application/pdf'];

export function DropZone({
  onFilesSelected,
  multiple = true,
  disabled = false,
}: DropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback((files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      // Check file type
      if (!ACCEPTED_MIME_TYPES.includes(file.type) && !file.name.toLowerCase().endsWith('.pdf')) {
        errors.push(`"${file.name}" is not a PDF file`);
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    } else {
      setError(null);
    }

    return validFiles;
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      if (disabled) return;

      const droppedFiles = validateFiles(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        onFilesSelected(multiple ? droppedFiles : [droppedFiles[0]]);
      }
    },
    [disabled, multiple, onFilesSelected, validateFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFiles = validateFiles(e.target.files);
        if (selectedFiles.length > 0) {
          onFilesSelected(multiple ? selectedFiles : [selectedFiles[0]]);
        }
      }
      // Reset input so same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [multiple, onFilesSelected, validateFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        inputRef.current?.click();
      }
    },
    [disabled]
  );

  return (
    <div className="space-y-2">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Drop zone for PDF files"
        className={`
          relative rounded-lg border-2 border-dashed p-8 text-center transition-all cursor-pointer
          ${disabled ? 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600' : ''}
          ${
            dragOver && !disabled
              ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400'
              : !disabled
                ? 'bg-gray-50 dark:bg-gray-900/40 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                : ''
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled}
          className="hidden"
          aria-hidden="true"
        />

        <div className="flex flex-col items-center">
          <div
            className={`
              p-3 rounded-full mb-4
              ${dragOver ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-gray-100 dark:bg-gray-700'}
            `}
          >
            {dragOver ? (
              <FileText size={32} className="text-primary-600 dark:text-primary-400" />
            ) : (
              <Upload size={32} className={disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'} />
            )}
          </div>

          <p className={`text-base mb-2 ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
            {dragOver ? (
              'Drop PDF files here'
            ) : (
              <>
                Drag and drop PDF files here, or{' '}
                <span className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                  browse
                </span>
              </>
            )}
          </p>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {multiple ? 'Multiple files allowed' : 'Single file only'} - Max {MAX_FILE_SIZE_MB}MB per file
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
}
